import { forwardRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { useChatImage } from '../../../hooks/useChatImage'
import { baseAttachmentClassName, baseIconClassName } from './styles'

/**
 * Every attachment type renders as this pill. Passing `onRemove` is what makes
 * it removable, so the remove affordance and the plain style never disagree.
 */
export const AttachmentStub = forwardRef<
  HTMLDivElement,
  {
    label: React.ReactElement | string
    icon?: React.ReactElement
    image?: string
    onRemove?: () => void
    className?: string
  }
>(({ label, icon, image, onRemove, className, ...props }, ref) => {
  const canRemove = typeof onRemove === 'function'
  const shouldRequestImage = !(
    typeof image === 'string' &&
    (image.startsWith('data') || image.startsWith('http') || image.startsWith('blob') || image.startsWith('icon'))
  )
  const { imageUrl, isImageLoading } = useChatImage(shouldRequestImage ? image : undefined)
  const [isImageLoaded, setIsImageLoaded] = useState(false)

  return (
    <div
      {...props}
      className={twMerge(baseAttachmentClassName(canRemove), 'relative select-none', className)}
      ref={ref}
    >
      {icon && (
        <div
          className={twMerge(
            baseIconClassName,
            canRemove
              ? 'flex items-center justify-center opacity-100 transition-opacity group-hover/attachment:opacity-0'
              : 'flex items-center justify-center',
          )}
        >
          {icon}
        </div>
      )}
      {image && (
        <div
          className={twMerge(
            'pointer-events-none aspect-square size-4 flex-shrink-0 overflow-hidden rounded-[5px]',
            canRemove ? 'opacity-100 transition-opacity group-hover/attachment:opacity-0' : '',
          )}
        >
          {shouldRequestImage && (!imageUrl || isImageLoading) ? (
            <div className={'h-full w-full bg-white/10 object-cover'} />
          ) : (
            <img
              src={imageUrl ?? image}
              className={twMerge(
                'h-full w-full object-cover transition-opacity',
                shouldRequestImage && !isImageLoaded ? 'opacity-0' : 'opacity-100',
              )}
              onLoad={() => setIsImageLoaded(true)}
            />
          )}
        </div>
      )}
      <span className={'font-regular truncate text-[12px]'}>{label}</span>
      {canRemove && (
        <div
          className={twMerge(
            'absolute bottom-0 left-0 top-0 aspect-square w-fit opacity-0 transition-opacity',
            'flex items-center justify-center',
            'pointer-events-none group-hover/attachment:pointer-events-auto',
            'group-hover/attachment:opacity-100',
          )}
        >
          <div
            className={'text-foreground-placeholder hover:text-foreground h-fit w-fit cursor-pointer transition-colors'}
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4.69922 13.8047C4.61328 13.7227 4.55469 13.6211 4.52344 13.5C4.49609 13.3828 4.49805 13.2676 4.5293 13.1543C4.56055 13.0371 4.61719 12.9375 4.69922 12.8555L8.04492 9.50391L4.69922 6.1582C4.61719 6.07617 4.56055 5.97852 4.5293 5.86523C4.50195 5.74805 4.50195 5.63086 4.5293 5.51367C4.56055 5.39648 4.61719 5.29492 4.69922 5.20898C4.78516 5.11914 4.88672 5.06055 5.00391 5.0332C5.12109 5.00586 5.23633 5.00781 5.34961 5.03906C5.4668 5.06641 5.56836 5.12109 5.6543 5.20312L9 8.55469L12.3516 5.20312C12.4766 5.07812 12.6328 5.01562 12.8203 5.01562C13.0078 5.01562 13.168 5.08008 13.3008 5.20898C13.4336 5.33789 13.498 5.49805 13.4941 5.68945C13.4941 5.87695 13.4316 6.0332 13.3066 6.1582L9.95508 9.50391L13.3066 12.8555C13.4316 12.9805 13.4941 13.1367 13.4941 13.3242C13.4941 13.5117 13.4297 13.6719 13.3008 13.8047C13.168 13.9375 13.0078 14.002 12.8203 13.998C12.6328 13.998 12.4766 13.9355 12.3516 13.8105L9 10.459L5.6543 13.8105C5.56836 13.8926 5.4668 13.9473 5.34961 13.9746C5.23633 14.0059 5.12109 14.0059 5.00391 13.9746C4.88672 13.9473 4.78516 13.8906 4.69922 13.8047Z"
                fill="currentColor"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  )
})
AttachmentStub.displayName = 'AttachmentStub'
